/* One framework's controls, and what each one draws evidence from.
 *
 * Reached by opening a row on `/compliance`. The subject is a question an auditor asks first — *what
 * does this framework actually check, and where does the platform get the answer* — and the answer is
 * three things in order: the size of the framework, its controls with the evidence each names, and
 * then the map from evidence kind to the part of the platform it is collected from.
 *
 * **A narrower framework is not an incomplete one**, which is the whole reason the last card lists all
 * six kinds and marks the unused ones rather than omitting them. Omitting would leave a reader unable
 * to tell "this framework does not ask about PII" from "this platform cannot answer about PII" — and
 * those are a framework fact and a deployment fact respectively.
 *
 * **No single-framework route exists and none is needed.** `GET /v1/compliance/frameworks` returns the
 * list with controls embedded, and it is already cached by the screen this was opened from — so this
 * page costs no request. Adding a by-id read would be a backend change for a screen that has the data.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type { Control } from '@/shared/api/generated/compliance';
import { EVIDENCE_KINDS } from '@/shared/api/generated/compliance';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { SCREEN_PATHS } from '@/shared/routes';

import { useFrameworks, usePacks } from '../api/compliance';
import { CoverageByDataset } from './CoverageByDataset';
import {
  EVIDENCE_KIND_TOTAL,
  evidenceKindCount,
  findFramework,
  kindUsage,
} from '../model/packs';

function ControlRow({ control }: { readonly control: Control }): ReactElement {
  const { t } = useTranslation();

  return (
    <tr className="lq-cp__packRow">
      <td className="lq-mono lq-nowrap">{control.code}</td>
      <td>
        <strong className="lq-cp__ctlTitle">{control.title}</strong>
        {/* `description` is optional in the contract, and absent is not empty: a control with no
            description is one nobody wrote one for, which is worth saying rather than leaving a gap
            a reader takes for a rendering fault. */}
        <div className="lq-cell__secondary">
          {control.description ?? t('compliance.control.noDescription')}
        </div>
      </td>
      <td>
        <div className="lq-cp__kinds">
          {control.evidence.map((kind) => (
            <span
              key={kind}
              className="lq-cp__kind lq-cp__kind--small"
              title={t(`compliance.evidence.${kind}`)}
            >
              {t(`compliance.evidenceName.${kind}`)}
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}

export function FrameworkControls(): ReactElement {
  const { t } = useTranslation();
  const { frameworkCode = '', frameworkVersion = '' } = useParams();
  const frameworks = useFrameworks();
  const packs = usePacks();

  const breadcrumb = (
    <div className="lq-cp__crumb">
      <Link to={SCREEN_PATHS.compliance}>{t('compliance.title')}</Link>
      {' / '}
      {frameworkCode}
    </div>
  );

  if (frameworks.isPending) {
    return (
      <div className="lq-cp lq-refscreen">
        {breadcrumb}
        <SkeletonRows rows={4} columns={3} />
      </div>
    );
  }

  if (frameworks.isError) {
    return (
      <div className="lq-cp lq-refscreen">
        {breadcrumb}
        <Alert tone="error" title={t('compliance.loadFailed')}>
          {frameworks.error.message}
        </Alert>
      </div>
    );
  }

  const framework = findFramework(frameworks.data, frameworkCode, frameworkVersion);

  if (framework === undefined) {
    /* Named rather than a bare 404, and both halves are in the message: a code that exists at
       another version is the likely mistake, and a reader who typed a URL needs to see which pair
       was not found. */
    return (
      <div className="lq-cp lq-refscreen">
        {breadcrumb}
        <EmptyState
          title={t('compliance.notFound.title')}
          guidance={t('compliance.notFound.body', {
            code: frameworkCode,
            version: frameworkVersion,
          })}
        />
      </div>
    );
  }

  const usage = kindUsage(framework);
  /* Against this framework's *code*, any version. A pack generated under 2023.1 is evidence about
     this framework even after 2024.1 is registered, and counting only the exact version would report
     zero for a framework the tenant has been generating packs against for a year. */
  const packCount = packs.isSuccess
    ? packs.data.filter((pack) => pack.framework_code === framework.code).length
    : null;

  return (
    <div className="lq-cp lq-refscreen">
      {breadcrumb}

      <div className="lq-cp__detailHead">
        <div className="lq-cp__detailTitle">
          <h1 className="lq-cp__h1">{framework.title}</h1>
          <div className="lq-cp__detailDesc">{framework.description}</div>
        </div>
        <span className="lq-cp__idChip lq-mono">
          {framework.code} {framework.version}
        </span>
      </div>
      <div className="lq-pagerule lq-pagerule--detail" />

      <div className="lq-cp__stats">
        <div className="lq-cp__stat">
          <div className="lq-cp__statKey">{t('compliance.stat.controls')}</div>
          <div className="lq-cp__statValue">{framework.controls.length}</div>
          <div className="lq-cp__statNote">{t('compliance.stat.controlsNote')}</div>
        </div>
        <div className="lq-cp__stat">
          <div className="lq-cp__statKey">{t('compliance.stat.kinds')}</div>
          <div className="lq-cp__statValue">
            {t('compliance.ofTotal', {
              count: evidenceKindCount(framework),
              total: EVIDENCE_KIND_TOTAL,
            })}
          </div>
          <div className="lq-cp__statNote">{t('compliance.stat.kindsNote')}</div>
        </div>
        <div className="lq-cp__stat">
          <div className="lq-cp__statKey">{t('compliance.stat.packs')}</div>
          {/* Never a zero while the read is in flight or failed: this figure is about the tenant's
              own history, and "0 packs" is a claim rather than a placeholder. */}
          <div className="lq-cp__statValue">
            {packCount ?? <span className="lq-muted">—</span>}
          </div>
          <div className="lq-cp__statNote">{t('compliance.stat.packsNote')}</div>
        </div>
      </div>

      <div className="lq-sect">
        <h2 className="lq-sect__title">{t('compliance.section.controls')}</h2>
      </div>

      {framework.controls.length === 0 ? (
        <EmptyState
          title={t('compliance.noControls.title')}
          guidance={t('compliance.noControls.body')}
        />
      ) : (
        <div className="lq-table-scroll lq-table-scroll--framed lq-cp__controls">
          <table className="lq-table lq-table--ledger">
            <caption className="lq-visually-hidden">
              {t('compliance.controlsCaption', { framework: framework.title })}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('compliance.column.code')}</th>
                <th scope="col">{t('compliance.column.control')}</th>
                <th scope="col">{t('compliance.column.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {framework.controls.map((control) => (
                <ControlRow key={control.code} control={control} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CoverageByDataset framework={framework} />

      <section className="lq-cp__map">
        <h3 className="lq-cp__mapHead">{t('compliance.kindMap.title')}</h3>
        {EVIDENCE_KINDS.map((kind) => {
          const used = usage.get(kind) ?? 0;
          return (
            <div key={kind} className="lq-cp__mapRow">
              <div
                className={
                  used > 0 ? 'lq-cp__mapLabel' : 'lq-cp__mapLabel lq-cp__mapLabel--unused'
                }
                title={kind}
              >
                {t(`compliance.evidenceName.${kind}`)}
              </div>
              <div className="lq-cp__mapBody">
                <div>{t(`compliance.evidence.${kind}`)}</div>
                <div className="lq-cp__mapUsed">
                  {used > 0
                    ? t('compliance.kindMap.used', { count: used })
                    : t('compliance.kindMap.unused')}
                </div>
              </div>
            </div>
          );
        })}
        <p className="lq-note">{t('compliance.kindMap.foot')}</p>
      </section>
    </div>
  );
}
